import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { authData } from "../../redux/auth/authSlice";
import styles from "./IaMarks.module.scss";
import config from "../../config/aws-exports";
import { API } from "aws-amplify";
import { Checkbox } from "@material-ui/core";
import swal from "sweetalert";
import Skeleton from "@material-ui/lab/Skeleton";
import { Typography } from "@mui/material";
import { Close } from "@material-ui/icons";

const IaMarks = (props) => {
  const { handleClose, rows } = props;
  const dispatch = useDispatch();
  const userDetails = useSelector(authData);

  // State variables
  const [selectedSemester, setSelectedSemester] = useState("1");
  const [selectedCourse, setSelectedCourse] = useState("0");
  const [semesters, setSemesters] = useState([]);
  const [coursesBySemester, setCoursesBySemester] = useState({});
  const [iaMarksData, setIaMarksData] = useState([]);
  const [selectedEids, setSelectedEids] = useState(new Set());
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pgmIaMarks, setPgmIaMarks] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");

  const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
  };

  const tdStyle = {
    padding: "8px",
    textAlign: "left",
    fontSize: "14px",
  };

  const thStyle = {
    backgroundColor: "var(--sub-heading-color)",
    padding: "8px",
    textAlign: "left",
    fontSize: "16px",
    color: "white",
  };

  useEffect(() => {
    getProgramsDetails();
  }, []);

  async function getProgramsDetails() {
    const bodyParam = {
      body: {
        bpid: userDetails.curprgcou?.bpid,
        oid: config.aws_org_id,
        pid: userDetails.curprgcou?.pid,
      },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    setLoading(true);

    try {
      const programs = await API.post(
        config.aws_cloud_logic_custom_name,
        `/getProgramsDetails`,
        bodyParam,
      );

      const groupedBySemester = programs.pcurriculum.reduce((acc, course) => {
        const sem = course.semester;

        acc[sem] = acc[sem] || [];
        acc[sem].push(course);

        return acc;
      }, {});

      const title = programs?.pname?.toLowerCase() || "";

      if (title.includes("b.sc") || title.includes("bsc")) {
        setPgmIaMarks(30);
      } else if (title.includes("diploma")) {
        setPgmIaMarks(50);
      } else {
        setPgmIaMarks(25);
      }

      const semestersList = Object.keys(groupedBySemester)
        .map(Number)
        .sort((a, b) => a - b)
        .map(String);

      setSemesters(semestersList);
      setCoursesBySemester(groupedBySemester);

      if (semestersList.length > 0) {
        const first = semestersList[0];

        setSelectedSemester(first);

        const topicids = groupedBySemester[first].map((c) => c.btid);

        getIaMarks(topicids);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
    } finally {
      setLoading(false);
    }
  }

  async function getIaMarks(topicids) {
    if (!topicids?.length) {
      setIaMarksData([]);
      setSelectedEids(new Set());
      setCheckedUsers([]);
      return;
    }

    const bodyParam = {
      body: {
        oid: config.aws_org_id,
        bpid: userDetails.curprgcou?.bpid,
        topicids,
      },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    setLoading(true);

    try {
      const response = await API.post(
        config.aws_cloud_logic_custom_name,
        `/getIaMarks`,
        bodyParam,
      );

      setIaMarksData(
        response.map((item) => ({
          ...item,
          topics: item.topics || {},
        })),
      );

      // Reset selection when loading new data
      setSelectedEids(new Set());
      setCheckedUsers([]);
    } catch (error) {
      console.error("Error in getIaMarks:", error);

      swal("Error", "Failed to load IA marks", "error");

      setIaMarksData([]);
      setSelectedEids(new Set());
      setCheckedUsers([]);
    } finally {
      setLoading(false);
    }
  }

  const handleSemesterChange = (e) => {
    const sem = e.target.value;

    setSelectedSemester(sem);
    setSelectedCourse("0");

    const topicids = coursesBySemester[sem]?.map((c) => c.btid) || [];

    getIaMarks(topicids);
  };

  const handleCourseChange = (e) => {
    const courseId = e.target.value;

    setSelectedCourse(courseId);

    if (courseId === "0") {
      const topicids =
        coursesBySemester[selectedSemester]?.map((c) => c.btid) || [];

      getIaMarks(topicids);
    } else {
      getIaMarks([courseId]);
    }
  };

  const toggleSelect = (item) => {
    const eid = item.EID;

    if (!eid) return;

    setSelectedEids((prev) => {
      const next = new Set(prev);

      if (next.has(eid)) {
        next.delete(eid);
      } else {
        next.add(eid);
      }

      return next;
    });

    setCheckedUsers((prev) => {
      const exists = prev.some((u) => u.EID === eid);

      if (exists) {
        return prev.filter((u) => u.EID !== eid);
      }

      return [
        ...prev,
        {
          EID: item.EID,
          UNAME: item.UNAME,
          EMAILID: item.EMAILID,

          Iamarks: item.topics?.[selectedCourse]?.Iamarks ?? "",

          topics: {
            [selectedCourse]: item.topics?.[selectedCourse] || {},
          },
        },
      ];
    });
  };

  const toggleSelectAll = () => {
    const allSelected =
      filteredData.length > 0 &&
      filteredData.every((item) => selectedEids.has(item.EID));

    setSelectedEids((prev) => {
      const next = new Set(prev);

      if (allSelected) {
        filteredData.forEach((item) => {
          next.delete(item.EID);
        });
      } else {
        filteredData.forEach((item) => {
          if (item.EID) {
            next.add(item.EID);
          }
        });
      }

      return next;
    });

    // Rebuild checkedUsers based on new selection
    setCheckedUsers((prev) => {
      const selectedMap = new Map(prev.map((u) => [u.EID, u]));

      if (allSelected) {
        filteredData.forEach((item) => {
          selectedMap.delete(item.EID);
        });
      } else {
        filteredData.forEach((item) => {
          if (!selectedMap.has(item.EID)) {
            selectedMap.set(item.EID, {
              EID: item.EID,
              UNAME: item.UNAME,
              EMAILID: item.EMAILID,

              Iamarks: item.topics?.[selectedCourse]?.Iamarks ?? "",

              topics: {
                [selectedCourse]: item.topics?.[selectedCourse] || {},
              },
            });
          }
        });
      }

      return Array.from(selectedMap.values());
    });
  };

  // --------------------------------------------------
  // MARKS CHANGE
  // --------------------------------------------------

  const handleMarksChange = (eid, courseId, e) => {
    const value = e.target.value === "" ? "" : Number(e.target.value);

    // Update main IA marks data
    setIaMarksData((prev) =>
      prev.map((item) =>
        item.EID === eid
          ? {
              ...item,

              topics: {
                ...item.topics,

                [courseId]: {
                  ...item.topics?.[courseId],

                  Iamarks: value,
                },
              },
            }
          : item,
      ),
    );

    // Update selected users
    setCheckedUsers((prev) =>
      prev.map((user) =>
        user.EID === eid
          ? {
              ...user,

              Iamarks: value,

              topics: {
                ...user.topics,

                [courseId]: {
                  ...user.topics?.[courseId],

                  Iamarks: value,
                },
              },
            }
          : user,
      ),
    );
  };

  // --------------------------------------------------
  // UPDATE / FREEZE
  // --------------------------------------------------

  const handleUpdateOrFreezeIaMarks = () => {
    if (checkedUsers.length === 0) {
      swal("Warning", "Please select at least one student", "warning");

      return;
    }

    for (const user of checkedUsers) {
      const marks = user.Iamarks;

      if (marks === "" || isNaN(marks)) {
        swal(
          "Warning",
          "All selected students must have valid marks",
          "warning",
        );

        return;
      }

      if (marks < 0 || marks > pgmIaMarks) {
        swal("Warning", `Marks must be between 0 and ${pgmIaMarks}`, "warning");

        return;
      }
    }

    swal({
      title: "Confirm",
      text: "Update and freeze IA marks for selected students?",
      icon: "warning",
      buttons: ["Cancel", "Yes, Save"],
      dangerMode: true,
    }).then((willSave) => {
      if (willSave) {
        updateOrFreezeIaMarks();
      }
    });
  };

  async function updateOrFreezeIaMarks() {
    const topicids =
      selectedCourse === "0"
        ? coursesBySemester[selectedSemester]?.map((c) => c.btid) || []
        : [selectedCourse];

    const bodyParam = {
      body: {
        oid: config.aws_org_id,
        bpid: userDetails.curprgcou?.bpid,
        topicids,
        users: checkedUsers,
      },

      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    setLoading(true);

    try {
      await API.post(
        config.aws_cloud_logic_custom_name,
        `/updateIaMarks`,
        bodyParam,
      );

      swal("Success", "IA marks updated and frozen", "success");

      handleClose();
    } catch (error) {
      console.error("Update failed:", error);

      swal("Error", "Failed to save IA marks", "error");
    } finally {
      setLoading(false);
    }
  }

  // --------------------------------------------------
  // SEARCH
  // --------------------------------------------------

  const filteredData = iaMarksData.filter((item) => {
    const courseData = item.topics?.[selectedCourse] || {};

    const searchStr = `
      ${item.UNAME || ""}
      ${item.EMAILID || ""}
      ${courseData.reason || ""}
    `.toLowerCase();

    return searchStr.includes(searchTerm.toLowerCase().trim());
  });

  const allSelected =
    filteredData.length > 0 &&
    filteredData.every((item) => selectedEids.has(item.EID));

  const someSelected =
    filteredData.some((item) => selectedEids.has(item.EID)) && !allSelected;

  return (
    <div className={styles.maincontainer}>
      {/* HEADER */}
      <div className={styles.iconcontainer}>
        <h3 className={styles.create}>IA Marks Update</h3>

        <Close className={styles.closebutton} onClick={handleClose} />
      </div>

      {/* SEMESTER + COURSE */}
      <div
        style={{
          display: "flex",
          gap: "1.5rem",
          marginBottom: "1rem",
        }}
      >
        {/* SEMESTER */}
        <div className={styles.field}>
          <h3 className={styles.select}>Semester</h3>

          <select
            value={selectedSemester}
            onChange={handleSemesterChange}
            style={{
              height: "36px",
              padding: "0 12px",
              fontSize: "15px",
              border: "none",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              borderRadius: "4px",
              minWidth: "160px",
            }}
          >
            {semesters.map((sem) => (
              <option key={sem} value={sem}>
                Semester {sem}
              </option>
            ))}
          </select>
        </div>

        {/* COURSE */}
        <div className={styles.field}>
          <h3 className={styles.select}>Course</h3>

          <select
            value={selectedCourse}
            onChange={handleCourseChange}
            style={{
              height: "36px",
              padding: "0 12px",
              fontSize: "15px",
              border: "none",
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              borderRadius: "4px",
              minWidth: "220px",
            }}
          >
            <option value="0">All Courses</option>

            {coursesBySemester[selectedSemester]?.map((item) => (
              <option key={item.btid} value={item.btid}>
                {item.ttitle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* LOADING */}
      {loading ? (
        <div style={{ padding: "2rem" }}>
          {[...Array(5)].map((_, i) => (
            <Typography key={i} variant="h3">
              <Skeleton />
            </Typography>
          ))}
        </div>
      ) : selectedCourse !== "0" ? (
        /* TABLE CONTAINER */
        <div
          style={{
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          }}
        >
          {/* TABLE HEADER */}
          <div
            style={{
              padding: "1rem 1.5rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #eee",
            }}
          >
            <h3 style={{ margin: 0 }}>
              {coursesBySemester[selectedSemester]?.find(
                (c) => c.btid === selectedCourse,
              )?.ttitle || "Selected Course"}
            </h3>

            {checkedUsers.length > 0 && (
              <button
                onClick={handleUpdateOrFreezeIaMarks}
                style={{
                  background: "var(--sub-heading-color)",
                  color: "white",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                Update & Freeze
              </button>
            )}
          </div>

          {/* SEARCH */}
          <div
            style={{
              padding: "1rem 1.5rem",
            }}
          >
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "10px 14px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
              }}
            />
          </div>

          {/* TABLE */}
          <table style={tableStyle}>
            <thead>
              <tr>
                {/* SELECT ALL */}
                <th
                  style={{
                    ...thStyle,
                    width: "60px",
                    textAlign: "center",
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                </th>

                <th
                  style={{
                    ...thStyle,
                    width: "22%",
                  }}
                >
                  Name
                </th>

                <th
                  style={{
                    ...thStyle,
                    width: "18%",
                  }}
                >
                  Marks
                </th>

                <th
                  style={{
                    ...thStyle,
                    width: "20%",
                  }}
                >
                  Status
                </th>

                <th
                  style={{
                    ...thStyle,
                    width: "35%",
                  }}
                >
                  Reason
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      ...tdStyle,
                      textAlign: "center",
                      padding: "2rem",
                    }}
                  >
                    No students found
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => {
                  const isSelected = selectedEids.has(item.EID);

                  const courseData = item.topics?.[selectedCourse] || {};

                  return (
                    <tr key={item.EID || index}>
                      {/* CHECKBOX */}
                      <td
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleSelect(item)}
                        />
                      </td>

                      {/* NAME */}
                      <td style={tdStyle}>{item.UNAME || "—"}</td>

                      {/* MARKS */}
                      <td style={tdStyle}>
                        <input
                          type="number"
                          min="0"
                          max={pgmIaMarks}
                          /*
                           * IMPORTANT:
                           * Do NOT convert the value
                           * using /25 * pgmIaMarks.
                           *
                           * The entered value is displayed
                           * directly.
                           */
                          value={
                            courseData?.Iamarks !== undefined &&
                            courseData?.Iamarks !== null
                              ? courseData.Iamarks
                              : ""
                          }
                          onChange={(e) =>
                            handleMarksChange(item.EID, selectedCourse, e)
                          }
                          disabled={
                            item.topics?.[selectedCourse]?.concernRaised !==
                            undefined
                              ? item.topics?.[selectedCourse]?.concernRaised ===
                                  "false" ||
                                item.topics?.[selectedCourse]?.concernRaised ===
                                  "Updated"
                              : item.topics?.[selectedCourse]?.showIaMarks ===
                                true
                          }
                          style={{
                            width: "80px",
                            padding: "6px 10px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                          }}
                        />
                      </td>

                      {/* STATUS */}
                      <td style={tdStyle}>
                        {courseData.concernRaised && (
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "13px",

                              background:
                                courseData.concernRaised === "true"
                                  ? "#ffebee"
                                  : courseData.concernRaised === "Updated"
                                    ? "#e8f5e9"
                                    : "#f5f5f5",

                              color:
                                courseData.concernRaised === "true"
                                  ? "#c62828"
                                  : courseData.concernRaised === "Updated"
                                    ? "#2e7d32"
                                    : "#555",
                            }}
                          >
                            {courseData.concernRaised === "true"
                              ? "Concern Raised"
                              : courseData.concernRaised === "Updated"
                                ? "Updated"
                                : "No Concern"}
                          </span>
                        )}
                      </td>

                      {/* REASON */}
                      <td style={tdStyle}>{courseData.reason || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            padding: "3rem",
            textAlign: "center",
            color: "#666",
          }}
        >
          Please select a course to view and update IA marks.
        </div>
      )}
    </div>
  );
};

export default IaMarks;
